import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

import { webPushApi } from '../api/webPush';
import {
  enablePushForCurrentBrowser,
  restorePushForCurrentBrowser,
  setApplicationBadge,
} from '../pwa/webPushClient';

const formatDate = (value) => new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

export default function WebNotificationsButton({ tenantId }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [configured, setConfigured] = useState(false);
  const [publicKey, setPublicKey] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mountedRef = useRef(true);

  const refresh = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const result = await webPushApi.list();
      if (!mountedRef.current) return;
      setItems(result.items || []);
      setUnreadCount(result.unread_count || 0);
      await setApplicationBadge(result.unread_count || 0);
      setError('');
    } catch (requestError) {
      if (!quiet && mountedRef.current) {
        setError(requestError?.response?.data?.detail || 'Не удалось загрузить уведомления');
      }
    } finally {
      if (!quiet && mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const initialize = async () => {
      try {
        const config = await webPushApi.getConfig();
        if (!mountedRef.current) return;
        setConfigured(Boolean(config.enabled));
        setPublicKey(config.public_key || null);
        if (config.enabled && Notification.permission === 'granted') {
          const subscription = await restorePushForCurrentBrowser();
          if (mountedRef.current) setSubscribed(Boolean(subscription));
        }
      } catch {
        // The notification list remains usable when push delivery is disabled.
      }
      await refresh();
    };
    initialize();

    const interval = window.setInterval(() => refresh({ quiet: true }), 60000);
    const onMessage = (event) => {
      if (event.data?.type === 'WEB_PUSH_RECEIVED') refresh({ quiet: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh({ quiet: true });
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tenantId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const notificationId = params.get('pwaNotification');
    if (!notificationId) return;
    webPushApi.markRead(notificationId).finally(() => {
      params.delete('pwaNotification');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      refresh({ quiet: true });
    });
  }, []);

  const enablePush = async () => {
    if (!publicKey) return;
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Уведомления запрещены в настройках браузера');
        return;
      }
      await enablePushForCurrentBrowser(publicKey);
      setSubscribed(true);
      setError('');
      await webPushApi.sendTest();
      window.setTimeout(() => refresh({ quiet: true }), 800);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Не удалось включить уведомления');
    } finally {
      setLoading(false);
    }
  };

  const openNotification = async (item) => {
    if (!item.read_at) await webPushApi.markRead(item.id);
    setAnchorEl(null);
    if (item.url) window.location.assign(item.url);
    else await refresh({ quiet: true });
  };

  const markAllRead = async () => {
    await webPushApi.markAllRead();
    setItems((current) => current.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() })));
    setUnreadCount(0);
    await setApplicationBadge(0);
  };

  return (
    <>
      <Tooltip title="Уведомления">
        <IconButton
          aria-label="Уведомления"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ border: '1px solid var(--border-color)', background: 'var(--surface-bg)' }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            {unreadCount ? <NotificationsActiveRoundedIcon /> : <NotificationsNoneRoundedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, width: { xs: 'calc(100vw - 24px)', sm: 390 }, maxHeight: 520, border: '1px solid var(--border-color)', borderRadius: 3 } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography fontWeight={750}>Уведомления</Typography>
          {unreadCount > 0 && <Button size="small" onClick={markAllRead}>Прочитать все</Button>}
        </Stack>
        <Divider />
        {error && <Alert severity="warning" sx={{ m: 1.5 }}>{error}</Alert>}
        {configured && !subscribed && 'Notification' in window && (
          <Box sx={{ p: 1.5 }}>
            <Button fullWidth variant="contained" onClick={enablePush} disabled={loading}>
              Включить push-уведомления
            </Button>
          </Box>
        )}
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>Новых уведомлений нет</Typography>
        ) : (
          <List disablePadding sx={{ overflowY: 'auto', maxHeight: 390 }}>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => openNotification(item)}
                sx={{ alignItems: 'flex-start', gap: 1.25, py: 1.35, backgroundColor: item.read_at ? 'transparent' : 'rgba(30, 111, 214, 0.07)' }}
              >
                <Box sx={{ width: 8, height: 8, mt: 0.8, borderRadius: '50%', bgcolor: item.read_at ? 'transparent' : 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={item.read_at ? 600 : 750}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{item.body}</Typography>
                  <Typography variant="caption" color="text.disabled">{formatDate(item.created_at)}</Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
